/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { Button } from "../../../components/ui/button";
import { useRadioNodesData } from "./hooks";
import RnDrawer from "./RnDrawer";
import { prepareNrCellDataGrid, prepareRadioNodeDataGrid, tableContainerSxProps } from "../helpers";

import { RadixSelectControl } from "../../../components/ui/radix-select";
import { STATUSLIST } from "../../../globals/globals";
import { useStoreDispatch } from "../../../store/hooks";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import { useSelector } from "react-redux";
import { confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { setConfirmDialog } from "../../../store/helpers/helperReducers";
import { IFullDeviceDetails, INrCell, IRadioNodesDataGridRow } from "../../../store/api/deviceConfig/types";
import { GridEventListener, GridRowParams, GridRowSelectionModel } from "@mui/x-data-grid";
import { DIALOGOPENFOR, statusRadioNodes } from "../../../globals/globals";
import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../../globals/constants";
import { isOpenRnCuDrawerSelector, isOpenRnDrawerSelector, noOfSelectedRowSelector, selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { prepareActionProps } from "../../../components/Actions/ActionHelper";
import { radioNodeOptions } from "../../../components/Actions/ActionOptions";
import Actions from "../../../components/Actions/Actions";
import { setNoOfSelectedRow, setOpenRnCuDrawer, setOpenRnDrawer, setSelectedRadioNodeRow } from "../../../store/api/devices/reducers";
import CuDrawer from "./CuDrawer";
import { Link } from "react-router-dom";
import { Icon } from "../../../components/Shared/Icon";
import { LOCAL_STORAGE, API } from '../../../globals/globals';
import { setOpenCellDrawer } from '../../../store/api/devices/reducers';
import { useGetCellKpiDetailsMutation } from '../../../store/api/devices/deviceApiSlice';
import { toTimespanRangeMs } from '../../../store/helpers/helpers';
import EmbeddedDrawer from '../../DeviceDetail/EmbeddedCell/EmbeddedDrawer';
import { isOpenCellDrawerSelector } from '../../../store/api/devices/selectors';
const RadioNodes: React.FC = () => {
  const dispatch = useStoreDispatch();
  const [cellKpiData, setCellKpiData] = React.useState<any[]>([]);
  const [getCellKpiDetails] = useGetCellKpiDetailsMutation();
  const {
    permissions,
    data,
    isLoading,
    radioNodesColumns,
    setStatus,
    deleteRnHandler,
    setSelectedRnRow,
    rowClicked,
    setRowClicked,
    selectedRowData,
    setSelectedRowData,
    setDataForPartialRefresh,
    nrList,
    partialRefreshFullRn,
  } = useRadioNodesData();

  const getDataFromApi = data?.data || [];
  const radioNodesList = getDataFromApi?.filter((el) => el?.ethernetId !== null) || [];

  const lastTriggerRef = React.useRef<{ len1: number, len2: number } | null>(null);

  React.useEffect(() => {
    const len1 = getDataFromApi?.length || 0;
    const len2 = radioNodesList?.length || 0;

    const last = lastTriggerRef.current;

    // Trigger if lengths are different, and either first run or previously equal
    if (len1 !== len2 && (!last || last.len1 === last.len2)) {
      partialRefreshFullRn();
    }

    // Update reference for next check
    lastTriggerRef.current = { len1, len2 };
  }, [getDataFromApi?.length, radioNodesList?.length, partialRefreshFullRn]);


  const rowsData: Array<IRadioNodesDataGridRow> = prepareRadioNodeDataGrid(radioNodesList) || [];
  
  
  const getDataFromNrList: INrCell[] = nrList?.data || [];
  const nrCellList = prepareNrCellDataGrid(getDataFromNrList) || [];

  let partialRefreshRequiredRNs: Array<IRadioNodesDataGridRow> = [];

  const { rnRowIds = [] } = useSelector(noOfSelectedRowSelector);


  // Filter rowsData to get candidaties for partial refresh
  if (rowsData && rowsData.length > 0) {
    partialRefreshRequiredRNs = rowsData.filter((item) => item.model === "" && statusRadioNodes?.some((status) => status === item.status));
  }

  React.useEffect(() => {
    if (partialRefreshRequiredRNs.length > 0) {
      const partialRefInterval = setInterval(() => {
        setDataForPartialRefresh(partialRefreshRequiredRNs);
      }, 6000);

      return () => clearInterval(partialRefInterval);
    } else setDataForPartialRefresh([]);
    // Dependency purposefully excluded to avoid endless loop condition: partialRefreshRequiredRNs and setDataForPartialRefresh
  }, [rowsData]);

  const noOfRows = data?.totalItems || 0;
  //const [NoOfSelectedRow, setNoOfSelectedRow] = React.useState(0);
  const [selectedRow, setSelectedRow] = React.useState<IRadioNodesDataGridRow>();
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  
  // Debug log for selectedDevice
  React.useEffect(() => {
    console.log('RadioNodes selectedDevice changed:', {
      objectKey: selectedDevice?.objectKey,
      nodeType: selectedDevice?.nodeType,
      serialNumber: selectedDevice?.serialNumber,
      name: selectedDevice?.name
    });
  }, [selectedDevice?.objectKey, selectedDevice?.nodeType]);
  
  const actionProps = prepareActionProps(selectedDevice?.objectKey!, selectedDevice.name, selectedRow, radioNodeOptions, "RadioNodes", false, null);
  const isOfflineDevice = selectedDevice?.configurationStatus?.toLowerCase() === "offline";

  // Fetch cell KPI data for performance icon
  React.useEffect(() => {
    console.log('RadioNodes KPI useEffect triggered', {
      objectKey: selectedDevice?.objectKey,
      nodeType: selectedDevice?.nodeType,
      hasGetCellKpiDetails: !!getCellKpiDetails
    });
    
    const fetchCellKpiData = async () => {
      if (!selectedDevice?.objectKey) {
        console.log('Skipping KPI fetch - no objectKey');
        return;
      }
      
      try {
        const timeSpan = toTimespanRangeMs(7 * 24 * 60 * 60 * 1000); // 7 days in milliseconds
        const args = {
          filters: `to=${timeSpan?.to}&from=${timeSpan?.from}&health=ALL`,
          siteName: selectedDevice.objectKey,
          nodeType: selectedDevice.nodeType === "SN" ? "lte" : "nr",
        };
        
        console.log('Fetching cell KPI with args:', args);
        const response: any = await getCellKpiDetails(args);
        console.log('Cell KPI fetch response:', response);
        if (response?.data) {
          console.log('Setting cell KPI data:', response.data);
          setCellKpiData(response.data);
        } else {
          console.log('No data in response:', response);
        }
      } catch (error) {
        console.error('Error fetching cell KPI data:', error);
      }
    };
    
    fetchCellKpiData();
  }, [selectedDevice?.objectKey, selectedDevice?.nodeType, getCellKpiDetails]);

  const openConfirmDialog = useSelector(confirmDialogSelector);
  const isOpenRnDrawer = useSelector(isOpenRnDrawerSelector);
  const isOpenRnCuDrawer = useSelector(isOpenRnCuDrawerSelector);

  const statusOptions = [
    { value: "__placeholder__", label: "STATUS" },
    ...STATUSLIST.map((s) => ({ value: s, label: s })),
  ];
  const [statusValue, setStatusValue] = React.useState("__placeholder__");
  const handleOnStatusSelect = (val: string) => {
    setStatusValue(val);
    setStatus(val === "__placeholder__" ? "" : val);
  };

  const handleRowClicked: GridEventListener<"rowClick"> = (params: GridRowParams<IRadioNodesDataGridRow>) => {
    if (!permissions?.hasOpCodeGroup("view_topology")) return;
    if (params?.row?.model?.toLowerCase() !== "unknown" && !!params?.row?.model) {
      setRowClicked(true);
      if (selectedDevice?.nodeType === "SN") {
        dispatch(setOpenRnDrawer(true));
      } else {
        dispatch(setOpenRnCuDrawer(true));
      }
      setSelectedRowData(params.row);
      dispatch(setSelectedRadioNodeRow(params.row));
    } else return;
  };
  const handleOnRowSelected = (rowIds: GridRowSelectionModel) => {
    dispatch(setNoOfSelectedRow({ rnRowIds: rowIds }));
    //dispatch(setNoOfSelectedRow({ rnRowIds: rowIds }));
    const rowIdLength = rowIds?.length;
    //setNoOfSelectedRow(rowIdLength);
    if (rowIdLength === 1) {
      setSelectedRow(() => rowsData.filter((item) => item.id === rowIds[0])[0]);
      setSelectedRnRow(() => rowsData.filter((item) => item.id === rowIds[0])[0]);
    }
  };

  const handleOnDelete = () => {
    if (selectedDevice?.nodeType === "SN") {
      const isLteCellActive = selectedRow?.radios?.some((el) => !!el?.cellId && el?.cellId !== null);
      if (isLteCellActive) {
        return dispatch(setConfirmDialog(DIALOGOPENFOR.RNCELLDELETEPOPUP));
      } else {
        return dispatch(setConfirmDialog(DIALOGOPENFOR.RADIONODE));
      }
    } else {
      const isNrCellActive = nrCellList?.some((el) => !!el?.radioNode && Number(el?.radioNode) === selectedRow?.id);
      if (isNrCellActive) {
        return dispatch(setConfirmDialog(DIALOGOPENFOR.RNCELLDELETEPOPUP));
      } else {
        return dispatch(setConfirmDialog(DIALOGOPENFOR.RADIONODE));
      }
    }
  };

  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  const handleOpenPerformance = (cellName: string) => {
    const cellDetails = cellKpiData?.find((kpi: any) => kpi?.cell_name === cellName);
    if (cellDetails) {
      dispatch(setOpenRnDrawer(false));
      dispatch(setOpenRnCuDrawer(false));
      dispatch(setOpenCellDrawer(true));
      (window as any).__selectedCellForPerformance = cellDetails;
    }
  };

  const handleRemoveCells = (actionName: string) => {
    if (!actionName) return;
    dispatch(setConfirmDialog(undefined));
  };

  return (
    <>
      {openConfirmDialog === DIALOGOPENFOR.RADIONODE && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={deleteRnHandler}>
          {`Are you sure you want to delete RN Node - ${selectedRow?.id}?`}
        </ConfirmDialog>
      )}
      {openConfirmDialog === DIALOGOPENFOR.RNCELLDELETEPOPUP && (
        <ConfirmDialog actionName={constStr.ok} handleClickAction={handleRemoveCells}>
          Cells are associated with this RadioNode, to delete first remove all cells from RN Node - <strong>{selectedRow?.id || "Unknown"}</strong>?
          <p className="mt-4">
            <Link
              className="nooption-link"
              to={`/device/${selectedDevice?.objectKey}/${selectedDevice?.nodeType === "SN" ? "ltecell" : "nrcell"}`}
              onClick={() => dispatch(setConfirmDialog(undefined))}
            >
              "Click"
            </Link>
            {` here to Remove cells.`}
          </p>
        </ConfirmDialog>
      )}
      {rowClicked && isOpenRnDrawer && (() => {
        const handleOpenPerformance = (cellName: string) => {
          // Find the cell details
          const cellDetails = cellKpiData.find((kpi: any) => kpi.cell_name === cellName);
          if (cellDetails) {
            // Close RN drawer first
            dispatch(setOpenRnDrawer(false));
            setRowClicked(false);
            // Open cell performance drawer
            dispatch(setOpenCellDrawer(true));
            // Store the cell details for the embedded drawer
            (window as any).__selectedCellForPerformance = cellDetails;
          }
        };
        
        
        return (
          <RnDrawer
            data={selectedRowData}
            closedButtonClicked={handleCloseButtonClicked} 
            rowsData={rowsData}
            boundFloorData={null}
            onUnbindRemote={undefined}
            cellKpiData={cellKpiData}
            onOpenPerformance={handleOpenPerformance}
          />
        );
      })()}
      {rowClicked && isOpenRnCuDrawer && (() => {
        return (
          <CuDrawer 
            data={selectedRowData} 
            closedButtonClicked={handleCloseButtonClicked} 
            rowsData={rowsData}
            boundRemote={null}
            boundFloorData={null}
            onUnbindRemote={undefined}
            cellKpiData={cellKpiData}
            onOpenPerformance={handleOpenPerformance}
          />
        );
      })()}
      <div className="flex flex-col">
        {rnRowIds?.length > 0 ? (
          <div className="pb-4">
            <div className="flex gap-2 items-center">
              <p className="text-sm text-foreground">{`${rnRowIds?.length} selected`}</p>

              {permissions?.hasOpCodeGroup("manage_topology") && rnRowIds?.length === 1 && (
                <>
                  <Button variant="secondary" size="sm"
                    onClick={handleOnDelete}
                  >
                    <Icon name="delete" size={16} />
                    Delete
                  </Button>
                  {!!actionProps && <Actions actionProps={actionProps} tableId="radioNode" />}
                </>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center pb-2">
              <div className="w-40" >
                <RadixSelectControl
                  value={statusValue}
                  onValueChange={handleOnStatusSelect}
                  options={statusOptions}
                  placeholder="STATUS"
                />
              </div>
            </div>
          </div>
        )}
        <div>
          {isLoading && <p>Loading...</p>}
          {!isLoading && (
            <CustomDataGrid
              tableId="radioNode"
              rows={rowsData}
              columns={radioNodesColumns}
              checkboxSelection={true}
              rowCount={noOfRows}
              loading={isLoading}
              disableColumnMenu={true}
              disableRowSelectionOnClick={true}
              onRowSelectionModelChange={handleOnRowSelected}
              rowSelectionModel={rnRowIds}
              getRowId={(row: IRadioNodesDataGridRow) => row.id}
              onRowClick={handleRowClicked}
              isRowSelectable={() => !isOfflineDevice}
              sx={{
                "& .MuiDataGrid-virtualScroller": { overflowY: "hidden" },
                "& .MuiDataGrid-scrollbar--vertical": { display: "none" },
              }}
              fillContainer={false}
            />
          )}
        </div>
      </div>
    </>
  );
};
export default RadioNodes;
